import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { AppShellLayoutComponent, ConfirmActionModalComponent } from '@clinic-platform/ui-components';
import { TranslatePipe } from '../i18n/translate.pipe';
import { Subscription } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { TwoFactorSetupModalService } from '../auth/two-factor-setup-modal.service';
import { ShellSidebarComponent } from './shell-sidebar.component';
import { ShellTopbarComponent } from './shell-topbar.component';
import { ShellContextFabComponent } from './shell-context-fab.component';
import { SystemAdminContextService } from '../context/system-admin-context.service';
import { TenantBrandingService } from '../branding/tenant-branding.service';

@Component({
  selector: 'app-shell-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    AppShellLayoutComponent,
    ConfirmActionModalComponent,
    TranslatePipe,
    ShellSidebarComponent,
    ShellTopbarComponent,
    ShellContextFabComponent
  ],
  template: `
    <div
      class="shell-layout"
      [style.--tenant-primary]="tenantPrimaryColor"
      [style.--tenant-secondary]="tenantSecondaryColor"
      [style.--tenant-accent]="tenantAccentColor"
      [style.--tenant-slot-available]="tenantSlotAvailableColor"
      [style.--tenant-slot-unavailable]="tenantSlotUnavailableColor"
      [style.--tenant-slot-pressure]="tenantSlotPressureColor"
      [style.--tenant-slot-past-pattern]="tenantSlotPastPatternColor"
      [style.--tenant-slot-past-pattern-opacity]="tenantSlotPastPatternOpacity"
      [style.--tenant-sidebar-bg]="tenantSidebarBackgroundColor"
      [style.--tenant-sidebar-text]="tenantSidebarTextColor"
      [style.--tenant-sidebar-overlay-opacity]="tenantSidebarOverlayOpacity"
      [style.--tenant-sidebar-bg-image]="tenantSidebarBackgroundImage"
      [style.--tenant-content-overlay-opacity]="tenantContentOverlayOpacity"
      [style.--tenant-content-bg-image]="tenantContentBackgroundImage"
      [style.--tenant-topbar-overlay-opacity]="tenantTopbarOverlayOpacity"
      [style.--tenant-topbar-bg-image]="tenantTopbarBackgroundImage"
    >
    <lib-app-shell-layout>
      <app-shell-sidebar appShellSidebar></app-shell-sidebar>
      <app-shell-topbar appShellTopbar></app-shell-topbar>
      @if (showOutlet) {
        <router-outlet></router-outlet>
      }
    </lib-app-shell-layout>
    <app-shell-context-fab />
    </div>

    <lib-confirm-action-modal
      [isOpen]="modalService.isOpen"
      [title]="'security.guard.modal.title' | t"
      [message]="modalService.message | t"
      [confirmText]="'security.guard.modal.confirm' | t"
      [cancelText]="'common.close' | t"
      (cancel)="modalService.close()"
      (confirm)="goToSecurity()"
    />
  `,
  styles: [``]
})
export class ShellLayoutComponent
implements OnInit, OnDestroy
{
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly tenantBranding = inject(TenantBrandingService);
  private readonly subscriptions = new Subscription();
  protected readonly modalService = inject(TwoFactorSetupModalService);
  protected showOutlet = true;
  protected tenantPrimaryColor = '#2563eb';
  protected tenantSecondaryColor = '#0f172a';
  protected tenantAccentColor = '#f59e0b';
  protected tenantSlotAvailableColor = '#22c55e';
  protected tenantSlotUnavailableColor = '#f3f4f6';
  protected tenantSlotPressureColor = '#f59e0b';
  protected tenantSlotPastPatternColor = '#64748b';
  protected tenantSlotPastPatternOpacity = '20%';
  protected tenantSidebarBackgroundColor = '#0f172a';
  protected tenantSidebarTextColor = '#ffffff';
  protected tenantSidebarOverlayOpacity = '0.88';
  protected tenantSidebarBackgroundImage = 'none';
  protected tenantContentOverlayOpacity = '0.93';
  protected tenantContentBackgroundImage = 'none';
  protected tenantTopbarOverlayOpacity = '0.82';
  protected tenantTopbarBackgroundImage = 'none';
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  public ngOnInit(): void
  {
    this.bindBranding();

    this.subscriptions.add(this.systemAdminContext.refresh$.subscribe(() =>
    {
      this.showOutlet = false;
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
      }
      this.refreshTimer = setTimeout(() => {
        this.showOutlet = true;
        this.refreshTimer = null;
      }, 0);
    }));
  }

  public ngOnDestroy(): void
  {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.subscriptions.unsubscribe();
  }

  private bindBranding(): void {
    this.applyBrandingForCurrentContext();

    if (this.authService.getCurrentRole() !== 'system_admin') {
      return;
    }

    this.subscriptions.add(
      this.systemAdminContext.companyId$.subscribe(() => {
        this.applyBrandingForCurrentContext();
      }),
    );
    this.subscriptions.add(
      this.systemAdminContext.scopeMode$.subscribe(() => {
        this.applyBrandingForCurrentContext();
      }),
    );
  }

  private applyBrandingForCurrentContext(): void {
    const role = this.authService.getCurrentRole();
    if (role === 'system_admin') {
      if (this.systemAdminContext.isGlobalScope()) {
        this.applyBrandingVars(null);
        return;
      }

      const companyId = this.systemAdminContext.getCompanyId();
      if (!companyId?.trim()) {
        this.applyBrandingVars(null);
        return;
      }

      this.subscriptions.add(
        this.tenantBranding.getBrandingByCompanyId(companyId).subscribe((branding) => {
          this.applyBrandingVars(branding);
        }),
      );
      return;
    }

    const companySlug = this.route.snapshot.paramMap.get('companySlug')?.trim().toLowerCase() ?? '';
    if (!companySlug) {
      this.applyBrandingVars(null);
      return;
    }

    this.subscriptions.add(
      this.tenantBranding.getBranding(companySlug).subscribe((branding) => {
        this.applyBrandingVars(branding);
      }),
    );
  }

  private applyBrandingVars(
    branding: import('../branding/tenant-branding.service').TenantBrandingVm | null,
  ): void {
    const vars = this.tenantBranding.toCssVars(branding);
    this.tenantPrimaryColor = vars['--tenant-primary'];
    this.tenantSecondaryColor = vars['--tenant-secondary'];
    this.tenantAccentColor = vars['--tenant-accent'];
    this.tenantSlotAvailableColor = vars['--tenant-slot-available'];
    this.tenantSlotUnavailableColor = vars['--tenant-slot-unavailable'];
    this.tenantSlotPressureColor = vars['--tenant-slot-pressure'];
    this.tenantSlotPastPatternColor = vars['--tenant-slot-past-pattern'];
    this.tenantSlotPastPatternOpacity = vars['--tenant-slot-past-pattern-opacity'];
    this.tenantSidebarBackgroundColor = vars['--tenant-sidebar-bg'];
    this.tenantSidebarTextColor = vars['--tenant-sidebar-text'];
    this.tenantSidebarOverlayOpacity = vars['--tenant-sidebar-overlay-opacity'];
    this.tenantSidebarBackgroundImage = vars['--tenant-sidebar-bg-image'];
    this.tenantContentOverlayOpacity = vars['--tenant-content-overlay-opacity'];
    this.tenantContentBackgroundImage = vars['--tenant-content-bg-image'];
    this.tenantTopbarOverlayOpacity = vars['--tenant-topbar-overlay-opacity'];
    this.tenantTopbarBackgroundImage = vars['--tenant-topbar-bg-image'];
  }

  protected goToSecurity(): void {
    this.modalService.close();
    const preferredSlug = this.authService.getPreferredCompanySlug();
    if (preferredSlug) {
      void this.router.navigate(['/', preferredSlug, 'security']);
      return;
    }

    void this.router.navigate(['/security']);
  }
}
